import React, { useEffect, useState } from "react";
import {
  Gift,
  Calendar,
  Clock,
  Users,
  CheckCircle2,
  Loader2,
  X,
  PlayCircle,
  AlertCircle,
  ChevronRight,
  Coins,
  BadgeCheck,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

// Cloudinary-hosted "How to Refer" walkthrough video
const REFERRAL_VIDEO_URL =
  "https://res.cloudinary.com/xlysm8ns/video/upload/v1790413248/Refer_and_Earn_arz6ob.mp4";
const REFERRAL_VIDEO_POSTER = "/assets/refer-steps-guide.png";

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const statusBadge = (status) => {
  const styles = {
    Pending: "bg-yellow-100 text-yellow-700",
    Approved: "bg-green-100 text-green-700",
    Rejected: "bg-red-100 text-red-700",
  };
  return styles[status] || "bg-gray-100 text-gray-700";
};

// ─────────────────────────────────────────────────────────────
// Step badge for Refer flow
// ─────────────────────────────────────────────────────────────
const StepBadge = ({ n }) => (
  <span className="w-6 h-6 rounded-full bg-[#221E45] text-white text-xs font-bold flex items-center justify-center shrink-0">
    {n}
  </span>
);

// ─────────────────────────────────────────────────────────────
// Auto-play video for the Refer Steps modal.
// Starts playing as soon as it mounts (i.e. as soon as the user
// clicks "How to Refer" and the modal opens). Muted+playsInline
// is required for browsers to allow autoplay; the user can unmute
// via the native controls.
// ─────────────────────────────────────────────────────────────
const AutoPlayVideo = ({ src, poster }) => {
  return (
    <div className="relative rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-black">
      <video
        src={src}
        poster={poster}
        controls
        autoPlay
        muted
        playsInline
        className="w-full h-auto"
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Refer Steps Modal — shown when user clicks "How to Refer"
// ─────────────────────────────────────────────────────────────
const ReferStepsModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-md flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#FC7A69]/10 rounded-lg flex items-center justify-center">
              <Gift className="w-4 h-4 text-[#FC7A69]" />
            </div>
            <h2 className="text-sm font-semibold text-[#221E45]">How to Refer</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Steps */}
        <div className="overflow-y-auto flex-1 px-5 py-5 space-y-5">
          {/* Step 1 */}
          {/* <div className="flex gap-3 items-start">
            <StepBadge n={1} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#221E45] mb-1">Show the Presentation to the Student</p>
              <p className="text-xs text-gray-500">
                Walk the student through the presentation slides and explain the benefits of our courses.
              </p>
            </div>
          </div> */}

          {/* <div className="flex gap-2 items-center px-1">
            <div className="h-px flex-1 bg-gray-100" />
            <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />
            <div className="h-px flex-1 bg-gray-100" />
          </div> */}

          {/* Step 2 */}
          <div className="flex gap-3 items-start">
            {/* <StepBadge n={1} /> */}
            <div className="flex-1">
              {/* <p className="text-sm font-semibold text-[#221E45] mb-2">Submit the Referral Form</p>
              <p className="text-xs text-gray-500 mb-3">
                After the student shows interest, fill in the referral details — batch, date and time — and submit.
              </p> */}
              {/* Guide video (plays automatically once the modal opens) */}
              <AutoPlayVideo src={REFERRAL_VIDEO_URL} poster={REFERRAL_VIDEO_POSTER} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-lg bg-[#FC7A69] text-white text-sm font-medium hover:bg-[#fb6a57] transition-colors"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Available Redemptions Modal
// ─────────────────────────────────────────────────────────────
const RedemptionsModal = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const POINTS_PER_BLOCK = 100;
  const AMOUNT_PER_BLOCK = 500;

  const fetchRedemptions = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/trainer/referrals/redemptions`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to load redemptions");
      setData(json.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRedemptions();
  }, []);

  const handleRedeem = async () => {
    setRedeeming(true);
    setSuccessMsg("");
    setError("");
    try {
      const res = await fetch(`${API_BASE}/trainer/referrals/redemptions/redeem`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Redemption failed");
      setSuccessMsg("Redemption request submitted successfully!");
      fetchRedemptions();
    } catch (err) {
      setError(err.message);
    } finally {
      setRedeeming(false);
    }
  };

  const earnedPoints = data?.earned_reward_points || 0;
  const history = data?.redemption_history || [];
  const eligibleBlocks = Math.floor(earnedPoints / POINTS_PER_BLOCK);
  const canRedeem = history.length < eligibleBlocks;

  const redemptionStatusStyle = (status) => {
    if (status === "paid") return "bg-green-50 text-green-700 border-green-200";
    return "bg-yellow-50 text-yellow-700 border-yellow-200";
  };

  const redemptionStatusLabel = (status) => {
    if (status === "paid") return "Paid";
    return "Unpaid";
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-lg flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#FC7A69]/10 rounded-lg flex items-center justify-center">
              <Coins className="w-4 h-4 text-[#FC7A69]" />
            </div>
            <h2 className="text-sm font-semibold text-[#221E45]">Available Redemptions</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <p className="text-sm">Loading…</p>
            </div>
          ) : error ? (
            <div className="p-3 rounded-lg bg-red-50 text-red-700 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" /> {error}
            </div>
          ) : (
            <>
              {successMsg && (
                <div className="p-3 rounded-lg bg-green-50 text-green-700 text-xs flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 shrink-0" /> {successMsg}
                </div>
              )}

              {/* Points summary */}
              <div className="bg-[#221E45]/5 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Total Earned Points</p>
                  <p className="text-2xl font-bold text-[#221E45] mt-0.5">{earnedPoints}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 font-medium">Eligible Blocks</p>
                  <p className="text-2xl font-bold text-[#FC7A69] mt-0.5">{eligibleBlocks}</p>
                </div>
              </div>

              {/* Redemption info */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-xs text-blue-700 font-medium mb-0.5">Redemption Rate</p>
                <p className="text-sm font-bold text-blue-800">100 Points → ₹500</p>
              </div>

              {/* Redemption history table */}
              {history.length > 0 ? (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Redemption History</p>
                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Date</th>
                          <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500">Amount</th>
                          <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {history.map((rec) => (
                          <tr key={rec.redemption_id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-2.5 text-xs text-gray-600">
                              {new Date(rec.created_at).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </td>
                            <td className="px-4 py-2.5 text-center text-xs font-semibold text-gray-700">
                              ₹{rec.redemption_amount}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${redemptionStatusStyle(rec.redemption_status)}`}
                              >
                                {rec.redemption_status === "paid" ? (
                                  <span className="flex items-center gap-1">
                                    <BadgeCheck className="w-3 h-3" />
                                    Paid
                                  </span>
                                ) : (
                                  "Unpaid"
                                )}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-400">
                  <Coins className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No redemptions yet.</p>
                  {eligibleBlocks > 0 && (
                    <p className="text-xs mt-1">You have {eligibleBlocks} block(s) available — click Redeem above.</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────
const ReferAndEarn = () => {
  const { user } = useAuth();

  // Referral history / stats
  const [referrals, setReferrals] = useState([]);
  const [loadingReferrals, setLoadingReferrals] = useState(true);
  const [totalReferrals, setTotalReferrals] = useState(0);
  const [totalEarnedPoints, setTotalEarnedPoints] = useState(0);

  // Refer form (per-batch)
  const [showForm, setShowForm] = useState(false);
  const [activeBatch, setActiveBatch] = useState(null);
  const [referralDate, setReferralDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [referralTimestamp, setReferralTimestamp] = useState(
    new Date().toTimeString().slice(0, 5)
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Modals
  const [showReferStepsModal, setShowReferStepsModal] = useState(false);
  const [showRedemptionsModal, setShowRedemptionsModal] = useState(false);

  // ── Fetch referrals ──────────────────────────────────────────
  const fetchReferrals = async () => {
    setLoadingReferrals(true);
    try {
      const res = await fetch(`${API_BASE}/trainer/referrals`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load referrals");
      setReferrals(data.data || []);
      setTotalReferrals(data.meta?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingReferrals(false);
    }
  };

  const fetchEarnedPoints = async () => {
    try {
      const res = await fetch(`${API_BASE}/trainer/referrals/redemptions`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load points");
      setTotalEarnedPoints(data.data?.earned_reward_points || 0);
    } catch (err) {
      console.error("Error fetching earned points:", err.message);
    }
  };

  useEffect(() => {
    fetchReferrals();
    fetchEarnedPoints();
  }, [user]);

  // ── Refer form handlers ──────────────────────────────────────
  const handleOpenForm = (batchItem) => {
    setActiveBatch(batchItem);
    setReferralDate(new Date().toISOString().split("T")[0]);
    setReferralTimestamp(new Date().toTimeString().slice(0, 5));
    setSuccessMsg("");
    setError("");
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setActiveBatch(null);
  };

  const handleSubmit = async () => {
    if (!activeBatch) {
      setError("No batch selected");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/trainer/referrals`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          batchNo: activeBatch.batch_no,
          batchStudents: activeBatch.students || [],
          referralDate,
          referralTimestamp,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to submit referral");
      setSuccessMsg("Referral submitted successfully — status: Pending");
      setShowForm(false);
      setActiveBatch(null);
      fetchReferrals();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-4">
        <div className="w-10 h-10 bg-[#FC7A69]/10 rounded-xl flex items-center justify-center">
          <Gift className="w-5 h-5 text-[#FC7A69]" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[#221E45]">Refer & Earn</h1>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6 flex-wrap">
        {/* How to Refer → opens step-by-step modal */}
        <button
          onClick={() => setShowReferStepsModal(true)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#221E45] text-white text-sm font-medium rounded-lg hover:bg-[#332b66] transition-all shadow-sm w-full sm:w-auto"
        >
          <PlayCircle className="w-4 h-4" />
          How to Refer
        </button>

        {/* Available Redemptions */}
        <button
          onClick={() => setShowRedemptionsModal(true)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-[#221E45] text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-all shadow-sm w-full sm:w-auto"
        >
          <Coins className="w-4 h-4" />
          Available Redemptions History
        </button>
      </div>

      {/* Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6 flex flex-col md:flex-row items-center gap-4 md:gap-8">
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Total Referrals Made
          </p>
          <p className="text-2xl font-bold text-[#221E45] mt-1">
            {loadingReferrals ? "—" : totalReferrals}
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Total Earned Points
          </p>
          <p className="text-2xl font-bold text-[#221E45] mt-1">
            {loadingReferrals ? "—" : totalEarnedPoints}
          </p>
        </div>
      </div>

      {successMsg && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm flex items-center">
          <CheckCircle2 className="w-4 h-4 mr-2" /> {successMsg}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* How to Refer Steps Modal */}
      {showReferStepsModal && (
        <ReferStepsModal onClose={() => setShowReferStepsModal(false)} />
      )}

      {/* Redemptions Modal */}
      {showRedemptionsModal && (
        <RedemptionsModal onClose={() => setShowRedemptionsModal(false)} />
      )}

      {/* Refer Form Modal */}
      {showForm && activeBatch && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-5 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-semibold text-[#221E45]">New Referral</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Batch #{activeBatch.batch_no} — {activeBatch.courseName}
                </p>
              </div>
              <button
                onClick={handleCloseForm}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close form"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                  <Users className="w-3.5 h-3.5 mr-1" /> Batch Students (
                  {activeBatch.students?.length || 0})
                </label>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                  {activeBatch.students?.length ? (
                    <ul className="text-sm text-gray-600 space-y-1">
                      {activeBatch.students.map((s, idx) => (
                        <li key={idx}>{s.name}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-400">No students found for this batch</p>
                  )}
                </div>
              </div>
              <div>
                <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                  <Calendar className="w-3.5 h-3.5 mr-1" /> Referral Date
                </label>
                <input
                  type="date"
                  value={referralDate}
                  onChange={(e) => setReferralDate(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FC7A69]/30"
                />
              </div>
              <div>
                <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                  <Clock className="w-3.5 h-3.5 mr-1" /> Referral Time
                </label>
                <input
                  type="time"
                  value={referralTimestamp}
                  onChange={(e) => setReferralTimestamp(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FC7A69]/30"
                />
              </div>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full flex items-center justify-center px-4 py-2.5 bg-[#FC7A69] text-white text-sm font-medium rounded-lg hover:bg-[#fb6a57] disabled:opacity-60 transition-all"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {submitting ? "Submitting..." : "Submit Referral"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Referral History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-[#221E45]">My Referral History</h2>
        </div>
        {loadingReferrals ? (
          <p className="p-5 text-sm text-gray-400">Loading...</p>
        ) : referrals.length === 0 ? (
          <p className="p-5 text-sm text-gray-400">No referrals logged yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {referrals.map((r) => (
              <div
                key={r.referral_id}
                className="px-5 py-3 flex items-center justify-between text-sm"
              >
                <div>
                  <p className="font-medium text-gray-700">{r.batch_no}</p>
                  <p className="text-xs text-gray-400">
                    {r.referral_date} • {r.batch_students?.length || 0} students
                  </p>
                </div>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge(r.status)}`}
                >
                  {r.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferAndEarn;